/**
 * Admin Management GraphQL Operations
 * Requirements: 10.1, 10.2, 10.3, 10.4
 */

// ============================================
// Tenant Queries
// ============================================

export const GET_TENANT = `
  query GetTenant($tenantId: ID!) {
    getTenant(tenantId: $tenantId) {
      tenantId
      name
      status
      userCount
      enabledFeatures
      createdAt
      updatedAt
    }
  }
`;

export const LIST_TENANTS = `
  query ListTenants($limit: Int, $nextToken: String) {
    listTenants(limit: $limit, nextToken: $nextToken) {
      items {
        tenantId
        name
        status
        userCount
        enabledFeatures
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ============================================
// Tenant Mutations
// ============================================

export const CREATE_TENANT = `
  mutation CreateTenant($input: CreateTenantInput!) {
    createTenant(input: $input) {
      tenantId
      name
      status
      userCount
      enabledFeatures
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_TENANT = `
  mutation UpdateTenant($tenantId: ID!, $input: UpdateTenantInput!) {
    updateTenant(tenantId: $tenantId, input: $input) {
      tenantId
      name
      status
      userCount
      enabledFeatures
      createdAt
      updatedAt
    }
  }
`;

export const DEACTIVATE_TENANT = `
  mutation DeactivateTenant($tenantId: ID!) {
    deactivateTenant(tenantId: $tenantId) {
      tenantId
      name
      status
      userCount
      enabledFeatures
      createdAt
      updatedAt
    }
  }
`;

// ============================================
// User Queries
// ============================================

export const GET_USER = `
  query GetUser($userId: ID!) {
    getUser(userId: $userId) {
      userId
      tenantId
      role
      cognitoSub
      email
      name
      status
      createdAt
      updatedAt
    }
  }
`;

export const LIST_USERS = `
  query ListUsers($tenantId: String!, $limit: Int, $nextToken: String) {
    listUsers(tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        userId
        tenantId
        role
        cognitoSub
        email
        name
        status
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_CURRENT_USER = `
  query GetCurrentUser {
    getCurrentUser {
      userId
      tenantId
      role
      cognitoSub
      email
      name
      status
      createdAt
      updatedAt
    }
  }
`;

// ============================================
// User Mutations
// ============================================

export const CREATE_USER = `
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      userId
      tenantId
      role
      cognitoSub
      email
      name
      status
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_USER = `
  mutation UpdateUser($userId: ID!, $input: UpdateUserInput!) {
    updateUser(userId: $userId, input: $input) {
      userId
      tenantId
      role
      cognitoSub
      email
      name
      status
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_USER = `
  mutation DeleteUser($userId: ID!) {
    deleteUser(userId: $userId) {
      userId
      message
    }
  }
`;

export const ASSIGN_ROLE = `
  mutation AssignRole($input: AssignRoleInput!) {
    assignRole(input: $input) {
      userId
      tenantId
      role
      cognitoSub
      email
      name
      status
      createdAt
      updatedAt
    }
  }
`;

export const DEACTIVATE_USER = `
  mutation DeactivateUser($userId: ID!) {
    deactivateUser(userId: $userId) {
      userId
      tenantId
      role
      cognitoSub
      email
      name
      status
      createdAt
      updatedAt
    }
  }
`;

// ============================================
// Platform Admin User Queries
// ============================================

export const PLATFORM_ADMIN_LIST_USERS = `
  query PlatformAdminListUsers($tenantId: String!, $limit: Int, $nextToken: String) {
    platformAdminListUsers(tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        userId
        tenantId
        role
        cognitoSub
        email
        name
        status
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ============================================
// Platform Admin User Mutations
// ============================================

export const PLATFORM_ADMIN_CREATE_USER = `
  mutation PlatformAdminCreateUser($tenantId: String!, $input: CreateUserInput!) {
    platformAdminCreateUser(tenantId: $tenantId, input: $input) {
      userId
      tenantId
      role
      cognitoSub
      email
      name
      status
      createdAt
      updatedAt
    }
  }
`;

export const PLATFORM_ADMIN_UPDATE_USER = `
  mutation PlatformAdminUpdateUser($tenantId: String!, $userId: ID!, $input: UpdateUserInput!) {
    platformAdminUpdateUser(tenantId: $tenantId, userId: $userId, input: $input) {
      userId
      tenantId
      role
      cognitoSub
      email
      name
      status
      createdAt
      updatedAt
    }
  }
`;

export const PLATFORM_ADMIN_DELETE_USER = `
  mutation PlatformAdminDeleteUser($tenantId: String!, $userId: ID!) {
    platformAdminDeleteUser(tenantId: $tenantId, userId: $userId) {
      userId
      message
    }
  }
`;

// ============================================
// Feature Management Operations
// ============================================

export const UPDATE_TENANT_FEATURES = `
  mutation UpdateTenantFeatures($tenantId: ID!, $input: UpdateTenantFeaturesInput!) {
    updateTenantFeatures(tenantId: $tenantId, input: $input) {
      tenantId
      name
      status
      userCount
      enabledFeatures
      createdAt
      updatedAt
    }
  }
`;

export const GET_TENANT_FEATURES = `
  query GetTenantFeatures($tenantId: ID!) {
    getTenantFeatures(tenantId: $tenantId) {
      tenantId
      enabledFeatures
    }
  }
`;

export const CHECK_TENANT_FEATURE_ACCESS = `
  query CheckTenantFeatureAccess($feature: String!) {
    checkTenantFeatureAccess(feature: $feature) {
      tenantId
      feature
      hasAccess
    }
  }
`;

export const GET_CURRENT_TENANT_FEATURES = `
  query GetCurrentTenantFeatures {
    getTenantFeatures {
      tenantId
      enabledFeatures
    }
  }
`;

// ============================================
// Files manager (platform admin)
// ============================================

export const GET_FILES_MANAGER_SETTINGS = `
  query GetFilesManagerSettings {
    getFilesManagerSettings {
      visionModelId
      extractionModelId
      availableModels {
        id
        label
        provider
        supportsVision
      }
      updatedAt
      updatedBy
    }
  }
`;

export const UPDATE_FILES_MANAGER_SETTINGS = `
  mutation UpdateFilesManagerSettings($input: UpdateFilesManagerSettingsInput!) {
    updateFilesManagerSettings(input: $input) {
      visionModelId
      extractionModelId
      availableModels {
        id
        label
        provider
        supportsVision
      }
      updatedAt
      updatedBy
    }
  }
`;

export const LIST_FILES_MANAGER_RUNS = `
  query ListFilesManagerRuns($tenantId: String!, $limit: Int, $nextToken: String) {
    listFilesManagerRuns(tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        invocationId
        tenantId
        jobId
        loanId
        fileId
        fileName
        status
        visionModelId
        extractionModelId
        startedAt
        finishedAt
        durationMs
        pageCount
        documentCount
        inputTokens
        outputTokens
        totalTokens
        stages {
          stage
          modelId
          inputTokens
          outputTokens
          requests
        }
        errorMessage
      }
      nextToken
    }
  }
`;
